import { TextToTextureTextAlign, TextToTextureTextBaseline } from "flavr-common";
import { CanvasTexture, Color, Material, MeshBasicMaterial } from "three";
import { MeshRenderable } from "../mesh/MeshRenderable";
import { FacetRenderer, isRenderableType, Renderable, RenderableUpdateTriggerState } from "../renderable/Renderable";
const tmpColor = new Color();
export class TextToTextureRenderable extends FacetRenderer {
    constructor(owner) {
        super(owner);
        this.owner = owner;
        this.canvas = document.createElement("canvas");
        this.context = this.canvas.getContext("2d");
        this.texture = new CanvasTexture(this.canvas);
        this.material = new MeshBasicMaterial({ map: this.texture });
        this.canvas.width = 250;
        this.canvas.height = 250;
    }
    destroy() {
        this.texture.dispose();
        this.material.dispose();
    }
    setVisible(visible) {
        this.material.visible = visible;
        this.material.needsUpdate = true;
    }
    onInit(facet) {
        this.facet = facet;
        this.applyTextureToRenderable();
    }
    updateFacet(facet) {
        if (!this.context)
            return;
        this.context.setTransform(1, 0, 0, 1, 0, 0);
        const newWidth = facet.params.width || 250;
        const newHeight = facet.params.height || 250;
        const isResized = this.canvas.width !== newWidth || this.canvas.height !== newHeight;
        this.canvas.width = newWidth;
        this.canvas.height = newHeight;
        // draw background
        this.fillStyle(facet.params.backgroundColor);
        this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);
        if (facet.params.text) {
            const metrics = this.context.measureText(facet.params.text);
            const textWidth = metrics.width;
            textWidth;
            this.textAlign(facet.params.textAlign);
            this.textBaseline(facet.params.textBaseline);
            const textSize = this.fontSize(facet.params.fontSize);
            this.fillStyle(facet.params.textColor);
            const lines = facet.params.text.split("\n");
            const lineHeight = textSize * 1.2;
            for (let i = 0, len = lines.length; i < len; ++i) {
                const line = lines[i];
                this.context.fillText(line, this.canvas.width / 2, this.canvas.height / 2 - ((len - 1) * lineHeight) / 2 + i * lineHeight);
            }
        }
        if (facet.params.elements) {
            const textSize = this.fontSize(facet.params.fontSize);
            for (const element of facet.params.elements) {
                this.drawTextElement(element, textSize * 2.75);
            }
        }
        if (isResized) {
            this.texture.dispose();
            this.texture = new CanvasTexture(this.canvas);
            this.material.map = this.texture;
            this.material.needsUpdate = true;
        }
        else {
            this.texture.needsUpdate = true;
        }
    }
    updateTransform() { }
    onOtherRenderableChanged(state, renderable) {
        switch (state) {
            case RenderableUpdateTriggerState.Changed:
                if (renderable instanceof Renderable) {
                    this.applyTextureToRenderable();
                }
                break;
            case RenderableUpdateTriggerState.Destroyed:
                break;
        }
    }
    applyTextureToRenderable() {
        const renderable = this.owner.renderable;
        if (!renderable) {
            return;
        }
        if (isRenderableType(MeshRenderable, renderable)) {
            this.applyTextureToMesh(renderable);
        }
    }
    applyTextureToMesh(renderabel) {
        if (!this.facet) {
            return;
        }
        if (!renderabel.meshInstance.isLoaded) {
            return;
        }
        renderabel.meshInstance.traverseMaterial(Material, ({}, mesh) => {
            mesh.material = this.material;
        });
    }
    drawTextElement(element, lineHeight) {
        if (!this.context)
            return;
        this.textAlign(element.textAlign);
        this.textBaseline(element.textBaseline);
        this.fontSize(element.fontSize);
        this.fillStyle(element.textColor);
        const lines = element.text.split("\n");
        for (let i = 0; i < lines.length; i++)
            this.context.fillText(lines[i].trim(), element.x, element.y + i * lineHeight);
    }
    textAlign(textAlign) {
        if (!this.context)
            return;
        if (textAlign === undefined)
            return;
        switch (textAlign) {
            case TextToTextureTextAlign.Center:
                this.context.textAlign = "center";
                break;
            case TextToTextureTextAlign.End:
                this.context.textAlign = "end";
                break;
            case TextToTextureTextAlign.Left:
                this.context.textAlign = "left";
                break;
            case TextToTextureTextAlign.Right:
                this.context.textAlign = "right";
                break;
            case TextToTextureTextAlign.Start:
                this.context.textAlign = "start";
                break;
            default:
                this.context.textAlign = "center";
        }
    }
    textBaseline(textBaseline) {
        if (!this.context)
            return;
        if (textBaseline === undefined)
            return;
        switch (textBaseline) {
            case TextToTextureTextBaseline.Alphabetic:
                this.context.textBaseline = "alphabetic";
                break;
            case TextToTextureTextBaseline.Bottom:
                this.context.textBaseline = "bottom";
                break;
            case TextToTextureTextBaseline.Hanging:
                this.context.textBaseline = "hanging";
                break;
            case TextToTextureTextBaseline.Ideographic:
                this.context.textBaseline = "ideographic";
                break;
            case TextToTextureTextBaseline.Middle:
                this.context.textBaseline = "middle";
                break;
            case TextToTextureTextBaseline.Top:
                this.context.textBaseline = "top";
                break;
            default:
                this.context.textBaseline = "middle";
        }
    }
    fontSize(size) {
        const fontSize = size || 12;
        if (!this.context)
            return fontSize;
        this.context.font = `normal ${fontSize}px Arial`;
        return fontSize;
    }
    fillStyle(color) {
        if (!this.context)
            return;
        if (color !== undefined) {
            tmpColor.setHex(color);
        }
        else {
            tmpColor.setRGB(0, 0, 0);
        }
        this.context.fillStyle = tmpColor.getStyle();
    }
}
